import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  title: string;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Rückfrage vor einer Aktion, die sich nicht zurücknehmen lässt.
 *
 * Bewusst ein eigenes Element statt `window.confirm`: Der Browser-Dialog kann
 * keinen erklärenden Text mit Struktur zeigen, und beim Löschen eines Kontakts
 * ist genau dieser Text die eigentliche Information.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  destructive = false,
  onConfirm,
  onCancel,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Der Fokus startet auf „Abbrechen“, nicht auf der zerstörerischen Aktion:
  // wer den Dialog mit der Leertaste wegklickt, soll nichts löschen.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      // Auch im Erfolgsfall zuruecksetzen: nicht jedes `onConfirm` schliesst
      // den Dialog. Wo es stattdessen eine Begruendung einblendet — „diese
      // Liste fuehrt eine Kampagne“, „aus dieser Kampagne ging schon Post
      // raus“ —, blieben sonst beide Knoepfe fuer immer gesperrt, und weil
      // `busy` auch die Escape-Taste sperrt, kaeme niemand mehr heraus.
      // Nach dem Ausbau der Komponente ist das kein Zustandssetzen mehr:
      // React 18 nimmt es auf einem entfernten Element wortlos hin.
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        {children && <div className="mt-3">{children}</div>}

        {error && (
          <p className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            ref={cancelRef}
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => void confirm()}
            disabled={busy}
          >
            {busy ? 'Einen Moment …' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
